import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// liczba dziesiętna (kropka/przecinek)
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

// ułamek zwykły / mieszany (bez kropki/przecinka)
const simplifyFraction = (num, den) => {
  const gcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    if (a === 0 && b === 0) return 1;
    while (b !== 0) {
      const t = b;
      b = a % b;
      a = t;
    }
    return a === 0 ? 1 : a;
  };
  const g = gcd(num, den);
  return { numerator: num / g, denominator: den / g };
};

const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (trimmed.includes(".") || trimmed.includes(",")) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else return null;
  }

  return simplifyFraction(num, den);
};

// format do 2 miejsc po przecinku, bez nieznaczących zer
const formatMax2 = (value) => {
  let s = value.toFixed(2);
  s = s.replace(/\.?0+$/, "");
  return s;
};

/* ======================= SCENARIUSZ PARTII ======================= */

const PARTY_NAMES = [
  "Partia Szczęścia i Rozsądku",
  "Partia Zdrowia i Higieny",
  "Partia Spokoju i Uśmiechu",
  "Partia Nauki i Fantazji",
  "Partia Kawy i Herbaty",
];

export function generateScenario() {
  // dwie różne partie
  const idx1 = randInt(0, PARTY_NAMES.length - 1);
  let idx2 = randInt(0, PARTY_NAMES.length - 2);
  if (idx2 >= idx1) idx2 += 1;

  const partyAName = PARTY_NAMES[idx1];
  const partyBName = PARTY_NAMES[idx2];

  // losowe wyniki w procentach (10–60), tak aby nie były równe
  let supportA = randInt(10, 60);
  let supportB = randInt(10, 60);
  while (supportB === supportA) {
    supportB = randInt(10, 60);
  }

  const bigger =
    supportA >= supportB
      ? { name: partyAName, support: supportA }
      : { name: partyBName, support: supportB };

  const smaller =
    supportA >= supportB
      ? { name: partyBName, support: supportB }
      : { name: partyAName, support: supportA };

  const diff = bigger.support - smaller.support; // > 0

  // losowo pytamy „o ile procent więcej” albo „o ile procent mniej”
  const askMore = Math.random() < 0.5;

  const baseSupport = askMore ? smaller.support : bigger.support;
  const relativePercent = (diff / baseSupport) * 100;

  return {
    partyAName,
    partyBName,
    supportA,
    supportB,
    biggerName: bigger.name,
    biggerSupport: bigger.support,
    smallerName: smaller.name,
    smallerSupport: smaller.support,
    diff,
    askMore,
    relativePercent,
  };
}

/* ======================= GENERATOR ZADANIA ======================= */

export function generateTask(scenario) {
  const sc = scenario || generateScenario();

  return {
    type: "PercentPartyRelativeTask",
    data: sc,
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const {
    partyAName,
    partyBName,
    supportA,
    supportB,
    biggerName,
    smallerName,
    askMore,
  } = data;

  const subjectName = askMore ? biggerName : smallerName;
  const referenceName = askMore ? smallerName : biggerName;
  const verb = askMore ? "wyższy" : "niższy";

  return (
    <p>
      W pewnych wyborach{" "}
      <strong>{partyAName}</strong> uzyskała wynik{" "}
      <InlineMath math={`${supportA}\\%`} />, a{" "}
      <strong>{partyBName}</strong> uzyskała wynik{" "}
      <InlineMath math={`${supportB}\\%`} />. (Pozostałe głosy otrzymały inne
      komitety.) O ile <strong>procent</strong> {verb} wynik uzyskała{" "}
      <strong>{subjectName}</strong> od{" "}
      <strong>{referenceName}</strong>?
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 25, 12.5, 12 1/2"
        label=""
        suffixLatex="\\%"
      />
    </div>
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.relativePercent;

  if (!userAnswer || !userAnswer.raw) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  const raw = String(userAnswer.raw).trim();

  let value = parseDecimalFromString(raw);

  if (value === null) {
    const frac = parseFractionFromString(raw);
    if (frac && frac.denominator !== 0) {
      value = frac.numerator / frac.denominator;
    }
  }

  if (value === null) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  const isCorrect = Math.abs(value - expected) <= 0.01;

  return { isCorrect, expected, actual: value, comment: null };
}

/* ======================= ANSWER & USER ANSWER ======================= */

export function renderAnswer({ data }) {
  const text = formatMax2(data.relativePercent);
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${text}\\%`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw || !String(userAnswer.raw).trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  const norm = String(userAnswer.raw).trim().replace(",", ".");
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${norm}\\%`} />
    </div>
  );
}

/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const {
    biggerName,
    smallerName,
    biggerSupport,
    smallerSupport,
    diff,
    askMore,
    relativePercent,
  } = data;

  const diffStr = formatMax2(diff);
  const relStr = formatMax2(relativePercent);

  const base = askMore ? smallerSupport : biggerSupport;
  const baseName = askMore ? smallerName : biggerName;

  const step1 = `\\Delta = ${biggerSupport}\\% - ${smallerSupport}\\% = ${diffStr}\\,\\text{p.p.}`;
  const step2 = `\\dfrac{${diffStr}}{${base}} \\cdot 100\\% = ${relStr}\\%`;

  const text1 = askMore
    ? `o ile procent wynik partii ${biggerName} jest wyższy od wyniku partii ${smallerName}`
    : `o ile procent wynik partii ${smallerName} jest niższy od wyniku partii ${biggerName}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Obliczamy różnicę wyników w punktach
        procentowych.
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2:</strong> Odnosimy tę różnicę do wyniku partii{" "}
        <strong>{baseName}</strong> ({base}
        <InlineMath math={"\\%"} />), bo pytamy, {text1}.
      </p>
      <BlockMath math={step2} />

      <p>
        Odpowiedź: około <InlineMath math={`${relStr}\\%`} />.
      </p>
    </div>
  );
}
